import { fade } from '@material-ui/core/styles';
import compareAsc from 'date-fns/compareAsc';
import compareDesc from 'date-fns/compareDesc';
import endOfYear from 'date-fns/endOfYear';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfYear from 'date-fns/startOfYear';
import sub from 'date-fns/sub';

import { colorsPalette } from '../../theme';
import {
  DetailedReportByPathwaysItem,
  DetailedReportByVideosItem,
  GroupByPeriod,
  PATHWAY_STATUSES,
  VIDEO_STATUSES,
} from '../../types';
import { getTimeRange, roundToDecimals, secondsToHms, translate } from '../../utils/helpers';
import { CellType, CSV_TABLE_DATA, GRAPH_TYPE, RADAR_USER_LIMIT } from './const';

export const getTimeRangeFilters = () => {
  const threeMonthsAgo = sub(new Date(), { months: 3 });
  const monthAgo = sub(new Date(), { months: 1 });
  const yearStarted = startOfYear(new Date());
  const lastYear = sub(new Date(), { years: 1 });
  const lastYearStarted = startOfYear(lastYear);
  const lastYearEnded = endOfYear(lastYear);
  const lastMonthRange = getTimeRange(monthAgo);
  const lastThreeMonthsRange = getTimeRange(threeMonthsAgo);
  const thisYearRange = getTimeRange(yearStarted);
  const lastYearRange = getTimeRange(lastYearStarted, lastYearEnded);

  return {
    all: {
      display: translate('timeFilters.all'),
      filter: null,
      groupBy: GroupByPeriod.Month,
    },
    lastMonth: {
      display: translate('timeFilters.lastMonth'),
      filter: lastMonthRange,
      groupBy: GroupByPeriod.Day,
    },
    lastThreeMonths: {
      display: translate('timeFilters.lastThreeMonths'),
      filter: lastThreeMonthsRange,
      groupBy: GroupByPeriod.Week,
    },
    thisYear: {
      display: translate('timeFilters.thisYear'),
      filter: thisYearRange,
      groupBy: GroupByPeriod.Month,
    },
    lastYear: {
      display: translate('timeFilters.lastYear'),
      filter: lastYearRange,
      groupBy: GroupByPeriod.Month,
    },
    custom: {
      display: translate('timeFilters.custom'),
      filter: null,
      groupBy: GroupByPeriod.Month,
    },
  };
};

export const transformLoginUserStat = (chosenGraphType, isMobile, loginUserStatGrByDates) => [
  {
    id: 'loginUserStat',
    color:
      chosenGraphType === GRAPH_TYPE.Logins && !isMobile ? colorsPalette.white : colorsPalette.blue,
    data: loginUserStatGrByDates.map((item) => ({
      x: item?.date,
      y: roundToDecimals(item?.logins),
    })),
  },
];

export const transformViewingHoursData = (chosenGraphType, isMobile, viewingHoursDataGrByDates) => [
  {
    id: 'viewingHours',
    color:
      chosenGraphType === GRAPH_TYPE.Hours && !isMobile ? colorsPalette.white : colorsPalette.blue,
    data: viewingHoursDataGrByDates.map((item) => ({
      x: item?.date,
      y: roundToDecimals(+item?.duration),
    })),
  },
];

export const transformVideosWatchedData = (chosenGraphType, isMobile, videosWatchedDataGrByDates) =>
  videosWatchedDataGrByDates.map((item) => ({
    x: item?.date,
    y: +item?.count,
    color:
      chosenGraphType === GRAPH_TYPE.Videos && !isMobile ? colorsPalette.white : colorsPalette.blue,
  }));

export const transformPathwayToTableData = (item, handleViewDetails) => ({
  id: item?.pathway?.id,
  image: item?.pathway?.image_url,
  pathway: {
    label: item?.pathway?.name,
    link: item?.pathway?.slug,
  },
  duration: item?.pathway?.duration,
  cpdAvailable: item?.pathway?.cpd_credits,
  usersStarted: item?.users_started,
  usersWatched: item?.users_watched,
  usersCompleted: item?.users_completed,
  count_videos: item?.pathway?.count_videos,
  viewDetails: handleViewDetails(item),
});

export const transformAdvancedPathwayToTableData = (item: DetailedReportByPathwaysItem) => ({
  id: `${item?.pathway_id}-${item?.learner?.id}`,
  pathwayName: item?.pathway_name,
  duration: item?.pathway_duration,
  learner: {
    label: item?.learner?.name,
    helper: item?.learner?.email,
  },
  learnerName: item?.learner?.name,
  email: item?.learner?.email,
  progress: Math.round(item?.progress),
  status: item?.status,
  testScore: item?.test_passed ? item.test_score : null,
});

export const transformAdvancedVideoToTableData = (item: DetailedReportByVideosItem) => ({
  id: `${item?.video?.id}-${item?.learner.id}`,
  videoTitle: item?.video?.title,
  author: item?.video?.user?.name,
  categories: item?.video?.categories?.[0]?.title || [],
  duration: item?.video?.duration || 0,
  learner: {
    label: item?.learner?.name,
    helper: item?.learner?.email,
  },
  learnerName: item?.learner?.name,
  email: item?.learner?.email,
  progress: Math.round(item?.progress),
  status: item?.completed ? VIDEO_STATUSES.COMPLETED : VIDEO_STATUSES.STARTED,
});

export const transformVideoToTableData = (item, handleViewDetails) => {
  const categories = item?.video_categories ? item.video_categories.split(',') : [];
  const category = categories.length > 0 ? categories[0] : '';
  return {
    id: item?.story_id,
    image: item?.image,
    video: {
      label: item?.title,
      link: item?.video_url,
    },
    presenter: item?.presenter,
    videoCategories: category,
    duration: item?.video_duration,
    usersStarted: item?.users_started,
    usersCompleted: item?.users_completed,
    viewDetails: handleViewDetails(item),
  };
};

export const transformVideosCategoryData = (
  listOfAreas,
  videosWatchedByCategoryData,
  filteredUsers,
  teams,
) =>
  listOfAreas.map((area) => {
    const areaId = area.id;
    const areaTitle = area.title;
    const userDataRes = {};

    const areaCount = videosWatchedByCategoryData
      ? videosWatchedByCategoryData.find((item) => item?.category?.id === areaId)
      : null;

    const usersData = filteredUsers.map((user) => {
      const teamId = parseInt(user?.team_id);
      const usersTeam = teams && teams?.find((team) => team?.id === teamId);
      return {
        id: +user?.id,
        name: user?.name,
        team: usersTeam?.name,
        count: 0,
      };
    });

    if (areaCount) {
      areaCount?.users?.forEach((user) => {
        const userId = user?.user_id;
        const index = usersData.findIndex((usersDataUser) => usersDataUser?.id === userId);
        if (index !== -1 && user?.count) {
          usersData[index].count = user?.count;
        }
      });
    }

    usersData.forEach((user) => {
      const keyName = filteredUsers?.length > RADAR_USER_LIMIT ? user?.team : user?.name;
      const currValue = userDataRes[keyName] || 0;
      userDataRes[keyName] = currValue + user?.count;
    });

    return {
      category: areaTitle,
      ...userDataRes,
    };
  });

export const createUsersTableData = (videosCategoryUsers, teamItems, usersStatJson) => {
  if (usersStatJson) {
    const usersStat = JSON.parse(usersStatJson);

    return videosCategoryUsers.reduce((data, user) => {
      const teams: string[] = [];
      teamItems.forEach((team) => {
        if (user?.teams.find((userTeam) => userTeam?.id === team?.value)) {
          teams.push(team?.display);
        }
      });
      const userStat = usersStat?.[user?.id];

      const team = teamItems.find((item) =>
        user?.teams?.find((userTeam) => userTeam?.id === item?.value),
      );

      return data.concat({
        id: user?.id,
        teams,
        // TODO: remove team
        team: team?.display,
        name: {
          display: user?.name,
          title: user?.email,
        },
        email: user?.email,
        videosStarted: userStat?.videos_started,
        videosCompleted: userStat?.videos_completed,
        pathwaysWatched: userStat?.pathways_watched,
        pathwaysCompleted: userStat?.pathways_completed,
        CPDearned: userStat?.cpd_available,
        hoursWatched:
          userStat?.hours_watched && Number(userStat?.hours_watched)
            ? Math.round(Number(userStat?.hours_watched) * 10) / 10
            : 0,
        loginSessions: userStat?.login_sessions,
        lastSessions: userStat?.last_session,
      });
    }, []);
  } else {
    return [];
  }
};

export const getDateFilters = () => {
  const threeMonthsAgo = sub(new Date(), { months: 3 });
  const monthAgo = sub(new Date(), { months: 1 });
  const yearStarted = startOfYear(new Date());
  const lastYear = sub(new Date(), { years: 1 });
  const lastYearStarted = startOfYear(lastYear);
  const lastYearEnded = endOfYear(lastYear);
  const thisYearRange = getTimeRange(yearStarted);
  const lastYearRange = getTimeRange(lastYearStarted, lastYearEnded);
  const lastMonthRange = getTimeRange(monthAgo);
  const lastThreeMonthsRange = getTimeRange(threeMonthsAgo);

  return {
    all: null,
    thisYear: thisYearRange,
    previousYear: lastYearRange,
    lastMonth: lastMonthRange,
    lastThreeMonths: lastThreeMonthsRange,
  };
};

export const getSingleUserFilter = (userId, dateFilter) => ({
  user_ids: userId,
  date: dateFilter,
  group_by: GroupByPeriod.Month,
});

export const transformUserViewingHoursData = (viewingHoursDataGrByDates) => [
  {
    id: 'viewingHours',
    color: colorsPalette.blue,
    data:
      viewingHoursDataGrByDates?.map((item) => ({
        x: item?.date,
        y: roundToDecimals(+item?.duration),
      })) || [],
  },
];

export const transformUserVideosWatchedData = (videosWatchedDataGrByDates) =>
  videosWatchedDataGrByDates.map((item) => ({
    x: item?.date,
    y: +item?.count,
    color: colorsPalette.blue,
  }));

export const transformUserVideosCategoryData = (
  listOfAreas,
  videosWatchedByCategoryData,
  userName,
) =>
  listOfAreas.map((area) => {
    const areaId = area.id;
    const areaTitle = area.title;
    const areaCount = videosWatchedByCategoryData
      ? videosWatchedByCategoryData.find((item) => item?.category?.id === areaId)
      : null;

    return {
      category: areaTitle,
      color: fade(colorsPalette.violet, 0.4),
      borderColor: colorsPalette.violet,
      [userName as string]: areaCount?.count || 0,
    };
  });

const statusSortOrder = {
  [PATHWAY_STATUSES.STARTED]: 1,
  [PATHWAY_STATUSES.WATCHED]: 2,
  [PATHWAY_STATUSES.COMPLETED]: 3,
};

export const sortData = (data, key, sortingKey, type, asc) => {
  if (type === CellType.Date) {
    return data.sort((a, b) => {
      if (asc) {
        return compareAsc(new Date(a[key] || 0), new Date(b[key] || 0));
      } else {
        return compareDesc(new Date(a[key] || 0), new Date(b[key] || 0));
      }
    });
  } else if (type === CellType.Status) {
    return data.sort((a, b) => {
      if (asc) {
        return statusSortOrder[a[key]] && statusSortOrder[a[key]] > statusSortOrder[b[key]]
          ? 1
          : -1;
      } else {
        return statusSortOrder[b[key]] && statusSortOrder[b[key]] > statusSortOrder[a[key]]
          ? 1
          : -1;
      }
    });
  }

  return data.sort((a, b) => {
    const aKey = sortingKey ? a[key][sortingKey] : a[key];
    const bKey = sortingKey ? b[key][sortingKey] : b[key];

    if (aKey < bKey) return asc ? -1 : 1;
    if (aKey > bKey) return asc ? 1 : -1;
    return 0;
  });
};

export const downloadCsv = (data: string | Blob, fileName: string): void => {
  let blob;
  if (typeof data === 'string') {
    blob = new Blob([data], { type: 'text/csv' });
  } else {
    blob = data;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadCsvDirectly = (url: string, fileName: string): void => {
  const a = document.createElement('a');
  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const dataToCsvString = (headingData, resData, tab): string => {
  const csvHeaders = headingData.reduce(
    (dataStr: string, item): string =>
      item.title ? (dataStr.length ? `${dataStr},${item.title}` : item.title) : dataStr,
    '',
  );
  const csvData = resData.reduce(
    (dataRows: string[], dataItem): string[] => {
      const row = CSV_TABLE_DATA[tab].reduce((itemRow: string, itemName: string): string => {
        let cell = dataItem[itemName];
        if (itemName === 'name') cell = dataItem.name.display;
        else if (itemName === 'teams') cell = dataItem.teams.join(', ');
        else if (itemName === 'duration') cell = secondsToHms(dataItem.duration);
        else if (itemName === 'pathway' || itemName === 'video') cell = dataItem[itemName].label;
        else if (itemName === 'testScore' || itemName === 'progress')
          cell = cell || cell === 0 ? cell : '';
        else if (itemName === 'lastSessions') {
          const date = dataItem.lastSessions
            ? parse(dataItem.lastSessions, 'yyyy-MM-dd HH:mm:ss', new Date())
            : '';
          cell = date ? format(date, 'HH:mm E, do MMM yyyy') : '';
        }
        return itemRow.length ? `${itemRow},"${cell}"` : `"${cell}"`;
      }, '');
      return [...dataRows, row];
    },
    [csvHeaders],
  );
  return csvData.join('\n');
};

export const handleExportDetailedData = async (
  url: string,
  body,
  title: string | null | undefined,
  getToken: () => Promise<string>,
) => {
  try {
    const token = await getToken();

    const headers = {
      Accept: '*/*',
      authorization: `Bearer ${token}`,
      'Content-type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: body,
    });

    if (response && response.status === 200) {
      const blob = await response.blob();

      const currDate = Math.floor(Date.now() / 1000);
      const fileName = `${title?.replace(/ /g, '_')}-${currDate}`;

      downloadCsv(blob, fileName);
    }
  } catch (e) {}
};
